const BOAT_TYPE_ORDER = ['4x', '4-', '4+', '4x+', '2x', '2-', '1x'];
const BOAT_TYPE_RANK = new Map(BOAT_TYPE_ORDER.map((boatType, index) => [boatType, index]));

function normalizeDashCharacters(value) {
    return String(value || '').replace(/[‐‑‒–—−]/g, '-');
}

function getEventTitleText(event) {
    return String(event?.title || event?.Title || '').trim();
}

function getEventTitleShortText(event) {
    return String(event?.titleShort || event?.TitleShort || '').trim();
}

function getEventGenderRank(event) {
    const shortToken = getEventTitleShortText(event)
        .split(/[.\s]+/)
        .map(token => token.trim().toLowerCase())
        .filter(Boolean)[0] || '';

    if (['op', 'o', 'open', 'x'].includes(shortToken)) return 0;
    if (['w', 'women', 'womens', 'woman', 'ladies', 'female', 'f'].includes(shortToken)) return 1;
    if (['mx', 'mixed'].includes(shortToken)) return 2;

    const title = getEventTitleText(event).toLowerCase();
    if (/^(open)\b/.test(title)) return 0;
    if (/^(women|woman|ladies|female)\b/.test(title)) return 1;
    if (/^mixed\b/.test(title)) return 2;

    // If no explicit marker is present, treat as Open by default.
    return 0;
}

function getEventCategoryRank(event) {
    const text = `${getEventTitleText(event)} ${getEventTitleShortText(event)}`.toLowerCase();

    if (/\b(junior|j\s*\d{1,2}|u\s*\d{1,2}|under\s*\d{1,2})\b/.test(text)) return 2;
    if (/\bmas(?:ters)?\b/.test(text)) return 1;
    // Treat everything that is neither masters nor junior as senior.
    return 0;
}

function getJuniorAgeValue(event) {
    const text = `${getEventTitleText(event)} ${getEventTitleShortText(event)}`.toLowerCase();
    const match = text.match(/\b(?:j|junior|u|under)\s*(\d{1,2})\b/);
    if (!match) return null;
    const age = Number(match[1]);
    return Number.isFinite(age) ? age : null;
}

function getExplicitBoatTypeToken(event) {
    const normalized = normalizeDashCharacters(`${getEventTitleShortText(event)} ${getEventTitleText(event)}`)
        .toLowerCase()
        .replace(/\s+/g, ' ');

    const match = normalized.match(/\b(4x\+|4x|4\+|4-|2x|2-|1x)\b/i);
    return match ? match[1].toLowerCase() : '';
}

function inferBoatTypeToken(event) {
    const explicit = getExplicitBoatTypeToken(event);
    if (explicit) return explicit;

    const seats = Number(event?.seats ?? event?.Seats ?? 0) || 0;
    const coxed = Boolean(event?.coxed ?? event?.Coxed);
    const title = getEventTitleText(event).toLowerCase();

    const looksLikeSculling = /\b(scull|sculls|single|double|doubles|quad|quads|quadruple)\b/.test(title);

    if (/\bsingle\b/.test(title) || seats === 1) {
        return '1x';
    }

    if (seats === 4) {
        if (looksLikeSculling) return coxed ? '4x+' : '4x';
        return coxed ? '4+' : '4-';
    }

    if (seats === 2) {
        if (looksLikeSculling) return '2x';
        return '2-';
    }

    return '';
}

function getEventBoatTypeRank(event) {
    const token = inferBoatTypeToken(event);
    if (BOAT_TYPE_RANK.has(token)) return BOAT_TYPE_RANK.get(token);
    return Number.MAX_SAFE_INTEGER;
}

export function compareEventsByDisplayOrder(a, b) {
    const genderDiff = getEventGenderRank(a) - getEventGenderRank(b);
    if (genderDiff !== 0) return genderDiff;

    const boatDiff = getEventBoatTypeRank(a) - getEventBoatTypeRank(b);
    if (boatDiff !== 0) return boatDiff;

    const categoryDiff = getEventCategoryRank(a) - getEventCategoryRank(b);
    if (categoryDiff !== 0) return categoryDiff;

    const categoryRank = getEventCategoryRank(a);
    if (categoryRank === 2) {
        const juniorAgeA = getJuniorAgeValue(a);
        const juniorAgeB = getJuniorAgeValue(b);
        if (juniorAgeA !== juniorAgeB) {
            if (juniorAgeA === null) return 1;
            if (juniorAgeB === null) return -1;
            return juniorAgeB - juniorAgeA;
        }
    }

    return getEventTitleText(a).localeCompare(getEventTitleText(b), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}

export function sortEventsByDisplayOrder(events) {
    return [...events].sort(compareEventsByDisplayOrder);
}
